import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from "react";
import axios from "axios";
import en from "./en.json";
import it from "./it.json";

type Locales = Record<string, Record<string, any>>;

const locales: Locales = {
  en,
  it,
};

interface I18nContextType {
  lang: string;
  setLang: (l: string) => void;
  t: (key: string) => string;
  get: (key: string) => any;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const detectBrowserLang = () =>
    typeof navigator !== "undefined" && navigator.language && navigator.language.startsWith("it")
      ? "it"
      : "en";

  const [lang, setLangState] = useState<string>(() => {
    try {
      if (typeof localStorage !== "undefined") {
        const stored = localStorage.getItem("lang");
        if (stored && locales[stored]) return stored;
      }
    } catch (err) {
      console.warn("Unable to access localStorage for language", err);
    }
    return detectBrowserLang();
  });

  const setLang = useCallback((l: string) => {
    setLangState(l);
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem("lang", l);
      }
      // Save language preference to backend
      axios.put("/api/profile", { language: l }).catch(err => {
        console.warn("Failed to save language preference to server:", err);
      });
    } catch (err) {
      console.warn("Unable to persist language preference", err);
    }
  }, []);

  const t = (key: string) => {
    const parts = key.split(".");
    let node: any = locales[lang] || locales["en"];
    for (const p of parts) {
      if (node && p in node) node = node[p];
      else return key;
    }
    if (typeof node === "string") return node;
    return key;
  };

  const get = (key: string) => {
    const parts = key.split(".");
    let node: any = locales[lang] || locales["en"];
    for (const p of parts) {
      if (node && p in node) node = node[p];
      else return undefined;
    }
    return node;
  };

  return (
    <I18nContext.Provider value={{ lang, setLang, t, get }}>{children}</I18nContext.Provider>
  );
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
};

export default I18nContext;
